/**
 * User Profile Modal
 * 
 * Displays user profile data (name, phone, country, city, address)
 * in the admin panel.
 */

import React from "react";
import { Button } from "@app/components/shared/Button";
import type { User } from "@app/services/adminUsersApi";
import "./UserProfileModal.css";

interface UserProfileModalProps {
  user: User;
  onClose: () => void;
}

export function UserProfileModal({ user, onClose }: UserProfileModalProps): JSX.Element {
  const profileFields: Array<{ label: string; value: string | null }> = [
    { label: "Full name", value: user.name },
    { label: "Email", value: user.email },
    { label: "Phone", value: user.phone },
    { label: "Country", value: user.country },
    { label: "City", value: user.city },
    { label: "Address", value: user.address },
  ];

  const hasAnyProfileData = profileFields.some(
    (f) => f.value && f.label !== "Email"
  );

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal__header">
          <h2>User Profile</h2>
          <button
            className="profile-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            x
          </button>
        </div>

        <div className="profile-modal__content">
          {!hasAnyProfileData ? (
            <p className="profile-modal__empty">
              No profile data has been submitted by this user yet.
            </p>
          ) : (
            <dl className="profile-modal__list">
              {profileFields.map((field) => (
                <div key={field.label} className="profile-modal__row">
                  <dt className="profile-modal__label">{field.label}</dt>
                  <dd className="profile-modal__value">
                    {field.value || "-"}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>

        <div className="profile-modal__footer">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default UserProfileModal;
